import {makeAutoObservable, runInAction} from 'mobx';
import {ElasticSearchResult, IStorePage_ViewProduct} from './storepage.def';
import filtersStore from './components/filters/filters-store/filters-store';
import {Portal} from '../util';
import {ga4_item} from '../checkout/i';
import {logger} from '../logger';
import {replaceOwnedStoreSchema} from './structured-data/store-product-schema';

declare var StoreID: string;
declare var StoreName: string;
declare var __company_name: string;
declare var marketing_store_visit: any;
declare var marketing_set_list_items: any;
declare var __is_my_frames: boolean;
declare var __price_slash_active: boolean;
var _didRich = false;
// Preserve the existing visit-event payload limit without truncating the click registry.
const STORE_VISIT_ITEM_LIMIT = 80;

class StorePageDataSource {
    public products: ElasticSearchResult;
    /** 1-based page that produced `products`. Stays put while a later request is in flight or has failed. */
    public acceptedPageNumber: number | null = null;
    public matOptions: string[] = [];
    public mouldingOptions: string[] = [];
    public allow_reviews = true;
    public getExtraData = true;
    public didGoogle = false;

    constructor() {
        makeAutoObservable(this);
        this.allow_reviews = !Portal?.DisableReviews;
    }

    get hasProducts() {
        return this.products && !!this.products.hits.total.value;
    }

    /** Every displayed card as a GA4 item; event payload limits are applied by their caller. */
    buildMarketingItems(): ga4_item[] {
        return this.displayedProducts().map((product, index) => {
            // item_name is the plain product name site-wide (product page, cart, purchase all send it);
            // the institution travels in item_category2, the store in item_list_name.
            return {
                item_id: product.ItemID,
                product_id: product.ProductID,
                item_name: product.Name,
                currency: 'USD',
                index,
                item_brand: __company_name,
                item_category: product.ProductTypeDisplayName,
                item_category2: product.fsi?.InstName || '',
                item_list_id: StoreID,
                item_list_name: StoreName,
                location_id: 'ChIJUcHLaPbi54kRtbVMrbSH4MU',
                price: product.fsi.Price_Retail,
                quantity: 1,
                item_variant: 'original',
            };
        });
    }

    /** select_item resolves clicked cards against this list, so it must follow every filter/page change. */
    registerMarketingItems() {
        if (typeof marketing_set_list_items !== 'function') return;
        try {
            marketing_set_list_items(this.buildMarketingItems());
        } catch (err) {
            logger.error(err);
        }
    }

    /** One store-page visit. Later result changes update the schema and the click list, not this visit. */
    async DoMarketingStorePage() {
        if (this.didGoogle) return;
        if (_didRich) return;

        this.didGoogle = true;
        _didRich = true;
        const arr = this.buildMarketingItems().slice(0, STORE_VISIT_ITEM_LIMIT);

        runInAction(() => {
            this.didGoogle = true;
            _didRich = true;
        });

        try {
            if (typeof marketing_store_visit === 'function') {
                marketing_store_visit(arr);
            }
        } catch (err) {
            logger.error(err);
        }
    }

    /** Cards currently on screen. An empty or hidden result is an empty list so the previous schema can be cleared. */
    displayedProducts(): IStorePage_ViewProduct[] {
        if (!this.hasProducts) return [];
        const hits = this.products?.hits?.hits;
        if (!Array.isArray(hits)) return [];
        return hits.map(hit => hit?._source).filter((source): source is IStorePage_ViewProduct => !!source);
    }

    syncStructuredData() {
        try {
            const doc = globalThis.document;
            if (!doc) return;
            // The cached server list is already omitted for MyFrames. Do not add one from the client.
            const myFrames = typeof __is_my_frames !== 'undefined' && __is_my_frames === true;
            const priceSlashActive = typeof __price_slash_active !== 'undefined' && __price_slash_active === true;
            const pageHref = typeof window !== 'undefined' && window.location ? window.location.href : '';

            replaceOwnedStoreSchema(doc, myFrames ? [] : this.displayedProducts(), {
                cdnDomain: Portal?.CDNDomain,
                pageHref,
                priceSlashActive,
            });
        } catch (err) {
            logger.error(err);
        }
    }

    /**
     * First product request failed before any accepted page. Remove the server ItemList so a failed
     * load does not leave MSRP / stale catalog markup that never gets replaced.
     * After a successful page exists, leave the schema for the cards still on screen.
     */
    clearStoreSchemaWhenNoAcceptedResults() {
        if (this.acceptedPageNumber != null) return;
        try {
            const doc = globalThis.document;
            if (!doc) return;
            const pageHref = typeof window !== 'undefined' && window.location ? window.location.href : '';
            replaceOwnedStoreSchema(doc, [], {
                cdnDomain: Portal?.CDNDomain,
                pageHref,
                priceSlashActive: false,
            });
        } catch (err) {
            logger.error(err);
        }
    }

    getFilterOptions() {
        filtersStore.getMatOptions();
        filtersStore.getMouldingOptions();
    }

    stripPaginationAriaAttributes() {
        globalThis.document?.querySelectorAll('#products ul.pagination').forEach(paginationList => {
            paginationList.removeAttribute('role');
            paginationList.removeAttribute('aria-label');
        });
    }

    getExtra() {
        this.syncStructuredData();

        if (this.hasProducts) {
            this.DoMarketingStorePage();
        }
        // marketing_store_visit also sets the registry from its capped payload. Replace it
        // afterwards with all displayed cards, and clear it when accepted results are empty.
        this.registerMarketingItems();

        if (this.getExtraData && this.products?.hits?.hits) {
            this.getFilterOptions();
            setTimeout(this.stripPaginationAriaAttributes, 10);
        }

        this.getExtraData = false;
    }
}

const storePageDataSource = new StorePageDataSource();

export default storePageDataSource;
